/**
 * Hooks de dados da Matriz de Transição (Brief 4 · Gestor · ADR-031 Eixo O).
 *
 * Reusa o CRUD real de Playbooks (apps/engine/src/modules/playbooks) — NÃO há
 * recurso/model/cap próprio de matriz; a policy vive inline em cada playbook:
 *   - GET   /v1/playbooks     (lista · a policy está em `transitionPolicy.pairs`) · gate `playbook-read`
 *   - PATCH /v1/playbooks/:id (substitui `transitionPolicy` do `from`)             · gate `playbook-author`
 *
 * A query é GATEADA por capability no `enabled` (não só no render) — sem
 * `playbook-read` o fetch nem dispara, evitando 403 vazando em toast (lição
 * "gatear o fetch, não só o render"). Query key própria (`transition-matrix-*`),
 * desacoplada da tela de Playbooks: as duas nunca montam juntas e a invalidação
 * fica contida nesta feature. O PATCH usa If-Match com a `version` do playbook
 * `from` (ADR-071); 409 (optimistic lock) propaga ao chamador, que trata de forma
 * não-destrutiva (refetch + aviso).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useCapability } from '../../hooks/use-capability';
import { api } from '../../lib/api-client';

import type { PlaybookDto } from '../playbooks/types';
import type { UseMutationResult } from '@tanstack/react-query';

const MATRIX_QUERY_KEY = 'transition-matrix-playbooks';
const STALE_TIME = 60_000;

export function useMatrixPlaybooks(): {
  data: PlaybookDto[];
  isLoading: boolean;
  isError: boolean;
  canRead: boolean;
} {
  const canRead = useCapability('playbook-read');
  const query = useQuery({
    // Só playbooks ATIVOS entram na matriz N×N — origem/destino vigentes.
    queryKey: [MATRIX_QUERY_KEY],
    queryFn: () => api.listPlaybooks({ status: 'active' }),
    enabled: canRead,
    staleTime: STALE_TIME,
    retry: 1,
  });

  return {
    data: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    canRead,
  };
}

/**
 * Atualiza a `transitionPolicy` de um playbook `from` (PATCH · gate `playbook-author`).
 * O chamador passa o objeto `transitionPolicy` JÁ recalculado (pairs com o par da
 * célula editada inserido/atualizado) — o backend SUBSTITUI o objeto inteiro. Em
 * sucesso invalida a lista (refetch traz a `version` incrementada para o próximo
 * If-Match). 409 propaga ao chamador.
 */
export function useUpdateTransitionPolicy(): UseMutationResult<
  PlaybookDto,
  unknown,
  { fromId: string; expectedVersion: number; transitionPolicy: PlaybookDto['transitionPolicy'] }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ fromId, expectedVersion, transitionPolicy }) =>
      api.updatePlaybook(fromId, expectedVersion, { transitionPolicy }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [MATRIX_QUERY_KEY] });
    },
  });
}
